import {IPresentation} from '../../incase-domain/presentation';
import {IGameConfig} from '../../incase-domain/proptypes/gameConfig';
import {ISlidePreview} from '../../incase-domain/proptypes/slidePreview';
import {
    CLEAR_ELEMENT_CLICKED,
    CLOSE_ASK_TO_END_PRESENTATION,
    OPEN_ASK_TO_END_PRESENTATION,
    SET_ELEMENTS_CLICKED,
} from '../actions/types/chalkBoardElements.actions.types';
import {
    TOGGLE_REDRAW,
    UPDATE_DRAW_ENABLED,
    UPDATE_FULL_PRESENTATION,
    UPDATE_PRES_LOADING_STATE,
    UPDATE_PRESENTATION,
    UPDATE_PRESENTATION_HAS_SLIDES,
    UPDATE_PRESENTATION_RUNNING,
} from '../actions/types/dashboard.actions.type';
import {
    ADVANCE_SEQUENCE_STEP,
    RESET_PRESENTATION_REDUCER,
    RESET_SEQUENCE_QUEUE,
    SET_ACTIVE_MOBILE_QUESTION,
    SET_PREVENT_CLICK_TO_NEXT_SLIDE,
    SET_SEQUENCE_QUEUE,
    UPDATE_ARROWS_DISABLED,
    UPDATE_CURRENT_SLIDE,
    UPDATE_LAST_SHOWED_SLIDE,
    UPDATE_PRESENTATION_PREVIEWS,
    UPDATE_SCALE,
} from '../actions/types/presentation.actions.types';
import {PresentationReducerTypes} from './types/presentation.reducer.types';

export interface SequenceQueueEntry {
    elementId: string;
}

export interface PresentationReducerState {
    presLoadingState: number;
    currentSlideIndex: number;
    lastShowedSlide: number;
    presentationList: [];
    // presentation with all slides, and no rules applied
    fullPresentation?: IPresentation;
    // presentation that we show to user. Rules are applied to it, so some slides may be hidden
    loadedPresentation?: IPresentation;
    activeMobileQuestionId: string;
    presentationStatus: {
        found: boolean;
        id: string;
    };
    hasSlides: boolean;
    drawEnabled: boolean;
    redrawFlag: boolean;
    running: boolean;
    gameConfig: IGameConfig;
    clickedElementIds: string[];
    askToEndPresentationModule: boolean;
    presentationPreviews: ISlidePreview[];
    scale: number;
    arrowsDisabled: boolean;
    dataCollection?: boolean;
    preventClickToNextSlide?: boolean;
    sequenceQueue: SequenceQueueEntry[];
    activeSequenceStep: SequenceQueueEntry | null;
}

export const preloadedState: PresentationReducerState = {
    currentSlideIndex: 0,
    presLoadingState: 0,
    lastShowedSlide: 0,
    presentationList: [],
    fullPresentation: undefined,
    loadedPresentation: undefined,
    activeMobileQuestionId: '',
    presentationStatus: {
        found: false,
        id: '',
    },
    hasSlides: false,
    drawEnabled: false,
    redrawFlag: false,
    running: false,
    gameConfig: {
        correctPointsPlus: 0,
        wrongPointsMinus: 0,
        gameClock: 60,
        isGamificationOn: true,
    },
    clickedElementIds: [],
    askToEndPresentationModule: false,
    presentationPreviews: [],
    scale: 1,
    arrowsDisabled: false,
    dataCollection: false,
    preventClickToNextSlide: false,
    sequenceQueue: [],
    activeSequenceStep: null,
};

export const presentationReducer = (
    state: PresentationReducerState = preloadedState,
    action: PresentationReducerTypes
): PresentationReducerState => {
    switch (action.type) {
        case UPDATE_PRESENTATION:
            if (action.payload.found) {
                return {
                    ...state,
                    loadedPresentation: action.payload,
                    presentationStatus: {found: true, id: action.payload._id},
                    dataCollection: action.payload.dataCollection,
                };
            }
            return {...state, loadedPresentation: action.payload};
        case UPDATE_FULL_PRESENTATION:
            return {...state, fullPresentation: action.payload};
        case UPDATE_PRES_LOADING_STATE:
            return {...state, presLoadingState: action.payload};
        case UPDATE_PRESENTATION_RUNNING:
            return {...state, running: action.payload};
        case UPDATE_CURRENT_SLIDE:
            return {...state, currentSlideIndex: action.payload};
        case UPDATE_LAST_SHOWED_SLIDE:
            return {...state, lastShowedSlide: action.payload};
        case UPDATE_PRESENTATION_HAS_SLIDES:
            return {...state, hasSlides: action.payload};
        case UPDATE_DRAW_ENABLED:
            return {...state, drawEnabled: action.payload};
        case TOGGLE_REDRAW:
            return {...state, redrawFlag: !state.redrawFlag};
        case UPDATE_SCALE:
            return {...state, scale: action.payload};
        case SET_ELEMENTS_CLICKED:
            return {...state, clickedElementIds: action.payload};
        case CLEAR_ELEMENT_CLICKED:
            return {...state, clickedElementIds: []};
        case OPEN_ASK_TO_END_PRESENTATION:
            return {...state, askToEndPresentationModule: true};
        case CLOSE_ASK_TO_END_PRESENTATION:
            return {...state, askToEndPresentationModule: false};
        case RESET_PRESENTATION_REDUCER:
            return {...preloadedState};
        case UPDATE_PRESENTATION_PREVIEWS:
            return {...state, presentationPreviews: action.payload};
        case UPDATE_ARROWS_DISABLED:
            return {...state, arrowsDisabled: action.payload};
        case SET_ACTIVE_MOBILE_QUESTION:
            return {...state, activeMobileQuestionId: action.payload};
        case SET_PREVENT_CLICK_TO_NEXT_SLIDE:
            return {...state, preventClickToNextSlide: action.payload};
        case ADVANCE_SEQUENCE_STEP: {
            const [current, ...remaining] = state.sequenceQueue;
            return {
                ...state,
                activeSequenceStep: current ?? null,
                sequenceQueue: remaining,
            };
        }
        case RESET_SEQUENCE_QUEUE:
            return {...state, sequenceQueue: [], activeSequenceStep: null};
        case SET_SEQUENCE_QUEUE:
            return {...state, sequenceQueue: action.payload, activeSequenceStep: null};
        default:
            return state;
    }
};
